import React from 'react';
import {Platform, Text} from 'react-native';

interface CurrencyFormatOption {
  showPositiveSymbol?: boolean;
  showNegativeSymbol?: boolean;
  currencySymbol?: 'руб' | '₽' | 'none';
}

export function numberFormatToRub(
  amount: number,
  options: CurrencyFormatOption = {
    showPositiveSymbol: false,
    showNegativeSymbol: true,
    currencySymbol: 'руб',
  },
) {
  if (isNaN(amount)) return;

  const isNegative = amount < 0;
  const absoluteAmount = Math.abs(amount / 100);

  const fixedAmount = Number(absoluteAmount).toFixed(2);

  const [integerPart, decimalPart] = fixedAmount.split('.');

  const formattedIntegerPart = integerPart
    .split('')
    .reverse()
    .join('')
    .match(/.{1,3}/g)
    ?.join(' ')
    .split('')
    .reverse()
    .join('');

  if (formattedIntegerPart) {
    const negativeSymbol = options.showNegativeSymbol ? '-' : '';
    const positiveSymbol = options.showPositiveSymbol ? '+' : '';
    const symbol = isNegative ? negativeSymbol : positiveSymbol;

    return (
      <Text>
        {`${symbol}${formattedIntegerPart}.${decimalPart}`}
        {options.currencySymbol !== 'none' && (
          <Text
            style={{
              fontFamily:
                options.currencySymbol === '₽'
                  ? Platform.select({
                      web: 'system-ui',
                      ios: 'System',
                      android: 'sans-serif',
                      default: undefined,
                    })
                  : undefined,
            }}>
            {` ${options.currencySymbol}`}
          </Text>
        )}
      </Text>
    );
  }

  return undefined;
}

// TODO: Replace using numberFormatToRub to formatCurrency in the whole project

interface FormatCurrencyOptions extends Omit<Intl.NumberFormatOptions, 'style' | 'currency' | 'currencyDisplay'> {
  currencyDisplay?: '₽' | 'руб.';
  fallback?: string;
  convertKopecksToRubles?: boolean;
}

export function formatCurrency(amount?: number | string, options?: FormatCurrencyOptions) {
  const {currencyDisplay = 'руб.', fallback = '—', convertKopecksToRubles = false, ...otherOptions} = options ?? {};

  if (amount == null || isNaN(Number(amount))) return fallback;

  const parsedAmount = parseFloat(String(amount));

  // Sanitize fraction digit options to avoid RangeError: maximumFractionDigits is out of range
  const sanitizeFractionDigits = (opts: Intl.NumberFormatOptions): Intl.NumberFormatOptions => {
    const clamp = (value: unknown) => {
      const n = Math.trunc(Number(value));
      if (Number.isNaN(n)) return undefined;
      // Per spec, fraction digits are typically 0..20
      if (n < 0) return 0;
      if (n > 20) return 20;
      return n;
    };

    let min = clamp((opts as any).minimumFractionDigits);
    let max = clamp((opts as any).maximumFractionDigits);

    if (typeof max === 'number' && typeof min !== 'number') {
      min = 0;
    }

    if (typeof min === 'number' && typeof max === 'number' && max < min) {
      max = min;
    }

    const sanitized: Intl.NumberFormatOptions = {};
    if (typeof min === 'number') sanitized.minimumFractionDigits = min;
    if (typeof max === 'number') sanitized.maximumFractionDigits = max;
    return sanitized;
  };

  const sanitizedFractionDigits = sanitizeFractionDigits(otherOptions);

  const {format} = new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    ...otherOptions,
    ...sanitizedFractionDigits,
  });

  let rubles = parsedAmount;

  if (convertKopecksToRubles) {
    rubles = parsedAmount / 100;
  }

  let formattedNumber = format(rubles);

  if (currencyDisplay !== '₽') {
    formattedNumber = formattedNumber.replace('₽', currencyDisplay);
  }

  return formattedNumber;
}
